import React, { useState, useEffect } from 'react';
import '@/App.css';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Analytics } from '@vercel/analytics/react';

// Import custom components
import Layout from '@/components/Layout';
import UserManagement from '@/pages/UserManagement';
import ChangePassword from '@/pages/ChangePassword';
import LPGReport from '@/pages/LPGReport';
import ManajemenPangkalan from '@/pages/ManajemenPangkalan';

// Remove trailing slash from BACKEND_URL to prevent double slash
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, '') || '';
const API = `${BACKEND_URL}/api`;

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [currentPage, setCurrentPage] = useState('lpg-report');

  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('currentUser');
    
    if (savedToken && savedUser) {
      setToken(savedToken);
      setCurrentUser(JSON.parse(savedUser));
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password
      });
      
      const { token: authToken, username: user, nama, role } = response.data;
      const userData = { username: user, nama, role };
      
      setToken(authToken);
      setCurrentUser(userData);
      localStorage.setItem('token', authToken);
      localStorage.setItem('currentUser', JSON.stringify(userData));
      setIsAuthenticated(true);
      toast.success(`Selamat datang, ${nama}!`);
      
      // Set default page based on role
      if (role === 'Admin') {
        setCurrentPage('user-management');
      } else {
        setCurrentPage('lpg-report');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login gagal');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setToken('');
    setCurrentUser(null);
    setUsername('');
    setPassword('');
    localStorage.removeItem('token');
    localStorage.removeItem('currentUser');
    toast.success('Logout berhasil');
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'lpg-report':
        return <LPGReport token={token} currentUser={currentUser} />;
      case 'outlet-management':
        if (['Admin', 'Manajer', 'Checker'].includes(currentUser?.role)) {
          return <ManajemenPangkalan />;
        }
        return <div className="p-8 text-center">Tidak memiliki akses</div>;
      case 'user-management':
        if (currentUser?.role === 'Admin') {
          return <UserManagement token={token} />;
        }
        return <div className="p-8 text-center">Tidak memiliki akses</div>;
      case 'change-password':
        return <ChangePassword token={token} />;
      default:
        return <div className="p-8 text-center">Halaman tidak ditemukan</div>;
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-100 p-4">
        <Toaster position="top-right" richColors />
        <Card className="w-full max-w-md shadow-2xl">
          <CardHeader className="space-y-2 px-4 md:px-6">
            <CardTitle className="text-2xl md:text-3xl font-bold text-center text-emerald-800">
              Portal Agen LPG PSO
            </CardTitle>
            <CardDescription className="text-center text-sm md:text-base">
              Sistem Manajemen LPG 3 kg Pertamina
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 md:px-6">
            <form onSubmit={handleLogin} className="space-y-4 md:space-y-6">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-sm font-medium">Username</Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="Masukkan username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="h-10 md:h-11"
                  data-testid="username-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                <PasswordInput
                  id="password"
                  placeholder="Masukkan password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-10 md:h-11"
                  data-testid="password-input"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full h-10 md:h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-sm md:text-base"
                data-testid="login-button"
              >
                Login
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <Toaster position="top-right" richColors />
      <Analytics />
      <Layout
        currentUser={currentUser}
        onLogout={handleLogout}
        currentPage={currentPage}
        onNavigate={setCurrentPage}
      >
        {renderPage()}
      </Layout>
    </>
  );
}

export default App;
